import axios from 'axios';
import { toast } from 'sonner';

/**
 * Resolve the backend base URL.
 *
 * Priority (highest first):
 *   1. `REACT_APP_BACKEND_URL` env var (used at build time)
 *   2. window.location.origin — fall back to the same domain the app is
 *      served from. This makes the same build work on any custom domain
 *      because the Emergent ingress already routes `/api/*` to the backend.
 *
 * If the build-time value points to a different host than the user is
 * currently browsing AND the current host looks like a real custom domain
 * (i.e. not the preview/emergent.host bundled URL), prefer the current
 * origin to avoid pointless cross-origin calls.
 */
function resolveBackendBase() {
  const buildTime = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return buildTime;
  const origin = window.location.origin;
  // If the build-time URL is empty, use the current origin.
  if (!buildTime) return origin;
  // If the user is browsing from a host that is NOT the build-time host,
  // prefer same-origin so we avoid cross-origin CORS preflights.
  try {
    const buildHost = new URL(buildTime).host;
    const currentHost = window.location.host;
    if (currentHost && buildHost && currentHost !== buildHost) {
      return origin;
    }
  } catch {
    // Malformed REACT_APP_BACKEND_URL — fall through to build-time value.
  }
  return buildTime;
}

const BACKEND_BASE = resolveBackendBase();

const api = axios.create({
  baseURL: `${BACKEND_BASE}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  // 15 s — long enough for a genuine cold-start warm-up (~5-10 s) but
  // short enough that a wedged origin doesn't leave the UI hanging
  // indefinitely. axios raises an `ECONNABORTED` on timeout which the
  // response interceptor treats the same as a network error.
  timeout: 15000,
});

// ---------------------------------------------------------------------------
// Per-request timeout override for file uploads
// ---------------------------------------------------------------------------
// The 15 s default is right for reads and small mutations but murderously
// wrong for uploading a 100+ MiB ZIP or a PDF attachment.  This request
// interceptor detects a multipart body and bumps the timeout to 15 min —
// unless the caller already set an explicit `timeout` override, in which
// case we respect it.  Applies retroactively to every upload in the app,
// including future ones added without thinking about the global default.
api.interceptors.request.use((config) => {
  const ct = (
    config?.headers?.["Content-Type"] ||
    config?.headers?.["content-type"] ||
    ""
  ).toString().toLowerCase();
  const bodyIsForm = typeof FormData !== "undefined" && config?.data instanceof FormData;
  const isMultipart = ct.startsWith("multipart/") || bodyIsForm;
  const isBinaryDownload = config?.responseType === "blob" || config?.responseType === "arraybuffer";
  if ((isMultipart || isBinaryDownload) &&
      (config.timeout === undefined || config.timeout === 15000)) {
    config.timeout = 15 * 60 * 1000;      // 15 min — plenty for big ZIPs on a bad wifi day
  }
  return config;
});

// ---------------------------------------------------------------------------
// Cloudflare 520 / cold-start resilience
// ---------------------------------------------------------------------------
// When the origin pod has been idle, the first request through Cloudflare
// often gets a 520/521/522/524 because the CF↔origin TCP connection went
// stale. A single retry after ~1.5 s warms the connection and almost
// always succeeds. Only retry idempotent requests (GET/HEAD/OPTIONS) and
// the `/auth/me` + `/auth/refresh` reads.
//
// `POST /auth/login` is deliberately EXCLUDED — a login should be either
// snappy or fail immediately with a clear message so the user can retry
// themselves. Adding auto-retry to it makes a wedged origin feel like a
// 30-second freeze.
const _SAFE_TO_RETRY_METHODS = new Set(["get", "head", "options"]);
const _SAFE_TO_RETRY_URLS = ["/auth/refresh", "/auth/me"];
const _MAX_TRANSIENT_RETRIES = 1;

function _isTransientOriginError(err) {
  const status = err?.response?.status;
  // Network / DNS / connection error → status is undefined.
  // 520-524 = CF-specific origin errors. 502/503/504 = generic upstream.
  return !status || (status >= 500 && status <= 599);
}

function _shouldRetry(config, err) {
  if (!_isTransientOriginError(err)) return false;
  const method = (config?.method || "get").toLowerCase();
  const url = config?.url || "";
  if (_SAFE_TO_RETRY_METHODS.has(method)) return true;
  return _SAFE_TO_RETRY_URLS.some((u) => url.endsWith(u));
}

function _sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Response interceptor: auto-refresh on 401, auto-retry on transient 5xx.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const url = error.config?.url || "";
    const isAuthEndpoint = url.includes("/auth/");

    if (error.response?.status === 403 && String(error.response?.data?.detail || "").startsWith("MFA_SETUP_REQUIRED") && window.location.pathname !== "/profile") {
      window.location.assign("/profile");
      return Promise.reject(error);
    }

    // 401 → try refresh once (existing behaviour)
    if (error.response?.status === 401 && !error.config._retry && !isAuthEndpoint) {
      error.config._retry = true;
      try {
        await axios.post(`${BACKEND_BASE}/api/auth/refresh`, {}, { withCredentials: true });
        return api(error.config);
      } catch (refreshErr) {
        // Refresh failed — let AuthContext handle the redirect on next render
        if (process.env.NODE_ENV !== "production") {
          console.warn("Token refresh failed:", refreshErr?.response?.status);
        }
      }
    }

    // 5xx / network → up to 1 backoff-retry for idempotent GETs + auth
    // reads. POSTs (including login) are NOT retried — the user retries.
    if (error.config && _shouldRetry(error.config, error)) {
      error.config._transientRetries = (error.config._transientRetries || 0) + 1;
      if (error.config._transientRetries <= _MAX_TRANSIENT_RETRIES) {
        // ~1.5 s before a single retry. Total worst-case wait for a
        // cold-start GET: 15 s (timeout) + 1.5 s + 15 s = ~31 s.
        const delay = 1500;
        if (process.env.NODE_ENV !== "production") {
          console.info(
            `Transient origin error (${error.response?.status || "network"}) on ${url} — ` +
            `retry ${error.config._transientRetries}/${_MAX_TRANSIENT_RETRIES} in ${delay}ms`
          );
        }
        await _sleep(delay);
        return api(error.config);
      }
    }

    // All retries exhausted (or the request wasn't retryable). If this is
    // a Cloudflare-origin error, REWRITE `error.response.data` in place so
    // every downstream `formatApiError(err.response.data?.detail)` shows a
    // friendly message instead of the raw CF text.
    _sanitizeCloudflareError(error);
    return Promise.reject(error);
  }
);

// Rewrite Cloudflare 5xx bodies so callers can't accidentally render the
// raw CF text (which happened when three different GETs on one page all
// caught the same 520 and toasted its `detail` verbatim).
function _sanitizeCloudflareError(error) {
  const status = error?.response?.status;
  if (!status || status < 500) return;
  const data = error.response.data;
  const looksLikeCf =
    (data && typeof data === "object" &&
      (data.cloudflare_error === true || data.ray_id || data.error_code === status)) ||
    (typeof data === "string" && /cloudflare/i.test(data));
  const isCfStatus = status === 520 || status === 521 || status === 522 || status === 523 || status === 524;
  const bodyMentionsCf = data && typeof data === "object"
    && typeof data.detail === "string" && /cloudflare/i.test(data.detail);

  if (looksLikeCf || isCfStatus || bodyMentionsCf) {
    // Blank the detail so downstream `formatApiError(err.response.data?.detail)`
    // yields "" and pages fall back to their own per-endpoint text (e.g.
    // "Failed to load batches"). Users never see raw Cloudflare wording.
    error.response.data = {
      detail: "",
      _cloudflareError: true,
      _originalStatus: status,
    };
    // Fire ONE global toast — sonner dedupes on `id`, so multiple concurrent
    // failures collapse into a single visible toast.  Requests with
    // `config._silent = true` (e.g. the login-page health warm-up ping)
    // skip the toast entirely because a background probe failing should
    // never surface UI noise.
    if (!error.config?._silent) {
      try {
        toast.error(
          "Server didn't respond in time — it may be waking up. Please try again in a moment.",
          { id: "origin-transient-error", duration: 6000 }
        );
      } catch {
        /* toast unavailable outside React tree */
      }
    }
  }
}

export default api;

// Check if error is an auth error (should not show toast)
export function isAuthError(err) {
  return err?.response?.status === 401 || err?.response?.status === 403;
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
